import Big from "big.js";
import { getShippingServicesForPackage } from "./shipping/getShippingServicesForPackage";
import { OriginShippingCountries, ShippingService, ShippingRate } from "./shipping/shippingRates.types";
import { VATRatePerCountry } from "./VATRatePerCountry";
import { temuShippingPolicyPerCountry } from "./temuShippingPolicyPerCountry";
import { SALE_CHANNEL, SALES_CHANNELS } from "./salesChannels";
import memoizee from 'memoizee'
import { PriceRoundingMethod } from "../codegen/graphql";

export function calculatePriceWithCommissions({
  cost,
  costIncludesVAT,
  commissionsOnFinalPrice,
  roundToNearestFiftyCents = false,
}: {
  cost: Big | number;
  costIncludesVAT: boolean;
  commissionsOnFinalPrice: {
    rate: Big | number;
    includeVAT: boolean;
  }[];
  roundToNearestFiftyCents?: boolean;
}) {
  let finalPrice = new Big(cost);

  if (costIncludesVAT) {
    finalPrice = finalPrice.div(1.2);
  }

  let commissionsCoeffs = new Big(1);
  commissionsOnFinalPrice.forEach((commission) => {
    let thisCommissionCoeff = new Big(commission.rate);
    if (commission.includeVAT) {
      thisCommissionCoeff = thisCommissionCoeff.div(1.2);
    }

    commissionsCoeffs = commissionsCoeffs.minus(thisCommissionCoeff);
  });

  
  // Put back VAT
  // 20% VAT reversed is / 1.2
  commissionsCoeffs = commissionsCoeffs.minus(
    new Big(.2).div(1.2)
  );
  // Apply commissions
  finalPrice = finalPrice.div(commissionsCoeffs);

  if (roundToNearestFiftyCents) {
    finalPrice = finalPrice
      .times(2)
      .round()
      .div(2);
  }

  return finalPrice;
}


export type ProductPrices = {
  listingKey: string;
  listingLabel: string;

  // Total price for customer
  retailPriceIncludingVAT: Big;
  retailPriceExcludingVAT: Big;
  retailPriceVAT: Big;

  // Listing price + extra shipping price = retail price
  // Displayed for customer excluding possible shipping cost
  listingPriceIncludingVAT: Big;
  listingPriceExcludingVAT: Big;
  listingPriceVAT: Big;
  // Additional shipping cost, at checkout
  extraShippingPriceIncludingVAT: Big;
  extraShippingPriceExcludingVAT: Big;
  extraShippingPriceVAT: Big;
  // Additional channel fee, at checkout
  extraFeeIncludingVAT: Big;
  extraFeeExcludingVAT: Big;
  extraFeeVAT: Big;

  // Price to push on the channel, some channels need base price, some need retail price
  setExternalPrice: Big;

  grossMarginIncludingVAT: Big;
  grossMarginExcludingVAT: Big;
  grossMarginVAT: Big;

  channelFeeIncludingVAT: Big;
  channelFeeExcludingVAT: Big;
  channelFeeVAT: Big;

  packingCostIncludingVAT: Big;
  packingCostExcludingVAT: Big;
  packingCostVAT: Big;

  productCostIncludingVAT: Big;
  productCostExcludingVAT: Big;
  productCostVAT: Big;

  shippingCostIncludingVAT: Big;
  shippingCostExcludingVAT: Big;
  shippingCostVAT: Big;


  returnShippingCostIncludingVAT: Big;
  returnShippingCostExcludingVAT: Big;
  returnShippingCostVAT: Big;
  
  shippingServiceSelected: ReturnType<typeof getShippingServicesForPackage>[0] | null;
}

export function calculatePricesForAllPlatformsAndDestinations(
  args: Omit<Parameters<typeof calculatePrices>[0], 'forPlatform' | 'destination'>
): {
  [platform in SALE_CHANNEL]: {
    [destination in string]: ProductPrices;
  }
} {
  return Object.entries(SALES_CHANNELS).reduce((acc, [platformKey, platform]) => {
    acc[platform.name as SALE_CHANNEL] = platform.deliveryCountries.reduce((acc, destination) => {
      acc[destination] = calculatePrices({
        ...args,
        forPlatform: platformKey as SALE_CHANNEL,
        destination,
      });
      return acc;
    }, {} as { [destination in string]: ProductPrices });
    return acc;
  }, {} as ReturnType<typeof calculatePricesForAllPlatformsAndDestinations>);
}


type CalculatePricesArgs = {
  marginPercentOnBaseCost: number; // float 2 decimals
  targetListingPriceIncludingVat: number; // float 2 decimals
  listingPriceRoundingMethod: null | PriceRoundingMethod;
  returnRate?: number; // float 2 decimals

  productCostExcludingVAT: number; // float 2 decimals
  packingCostExcludingVAT: number; // float 2 decimals

  forPlatform: SALE_CHANNEL;
  supplierName: string;
  origin: OriginShippingCountries;
  destination: string;
  packageWeight: number;
  packageSize1: number;
  packageSize2: number;
  packageSize3: number;
}

function calculatePricesFn(
  args: NullableProperties<CalculatePricesArgs, 'marginPercentOnBaseCost' | 'targetListingPriceIncludingVat'>
): ProductPrices {
  let prices: Omit<ProductPrices, 'listingKey' | 'listingLabel'>;
  if (args.marginPercentOnBaseCost) {
    prices = calculatePrices_WithMarginPercentOnBaseCostFn(args as Omit<CalculatePricesArgs, 'targetListingPriceIncludingVat'>);
  }
  else if (args.targetListingPriceIncludingVat) {
    prices = calculatePrices_WithTargetPriceFn(args as Omit<CalculatePricesArgs, 'marginPercentOnBaseCost'>);
  }
  else {
    throw new Error('Either marginPercentOnBaseCost or targetListingPriceIncludingVat must be provided');
  }

  return {
    ...prices,
    listingKey: `${args.forPlatform}-${args.destination}`,
    listingLabel: `${args.forPlatform} - ${args.destination}`,
  };
}


function calculatePrices_WithMarginPercentOnBaseCostFn({
    marginPercentOnBaseCost,
    listingPriceRoundingMethod,
    productCostExcludingVAT: productCostExcludingVATArg,
    packingCostExcludingVAT: packingCostExcludingVATArg,

    forPlatform,
    supplierName,
    origin,
    destination,

    packageWeight,
    packageSize1,
    packageSize2,
    packageSize3,
  }: Omit<CalculatePricesArgs, 'targetListingPriceIncludingVat'>
): Omit<ProductPrices, 'listingKey' | 'listingLabel'> {
  
  // Channel fee and sales tax
  const countryVATRate = VATRatePerCountry[destination];
  const temuShippingPolicy = temuShippingPolicyPerCountry[destination];

  let retailPriceIncludingVAT = new Big(0);
  let retailPriceExcludingVAT = new Big(0);
  let retailPriceVAT = new Big(0);

  let listingPriceIncludingVAT = new Big(0);
  let listingPriceExcludingVAT = new Big(0);
  let listingPriceVAT = new Big(0);

  let extraShippingPriceIncludingVAT = new Big(0);
  let extraShippingPriceExcludingVAT = new Big(0);
  let extraShippingPriceVAT = new Big(0);

  let extraFeeIncludingVAT = new Big(0);
  let extraFeeExcludingVAT = new Big(0);
  let extraFeeVAT = new Big(0);

  let setExternalPrice = new Big(0);


  let grossMarginIncludingVAT = new Big(0);
  let grossMarginExcludingVAT = new Big(0);
  let grossMarginVAT = new Big(0);

  let channelFeeIncludingVAT = new Big(0);
  let channelFeeExcludingVAT = new Big(0);
  let channelFeeVAT = new Big(0);

  let packingCostIncludingVAT = new Big(packingCostExcludingVATArg).times(countryVATRate.applyCoeff);
  let packingCostExcludingVAT = new Big(packingCostExcludingVATArg);
  let packingCostVAT = packingCostIncludingVAT.minus(new Big(packingCostExcludingVATArg));

  let productCostIncludingVAT = new Big(productCostExcludingVATArg).times(countryVATRate.applyCoeff);
  let productCostExcludingVAT = new Big(productCostExcludingVATArg);
  let productCostVAT = productCostIncludingVAT.minus(productCostExcludingVATArg);

  let shippingCostIncludingVAT = new Big(0);
  let shippingCostExcludingVAT = new Big(0);
  let shippingCostVAT = new Big(0);

  let shippingServiceSelected: ReturnType<typeof getShippingServicesForPackage>[0] | null = null;

  // Our margin
  grossMarginIncludingVAT = productCostExcludingVAT
    .plus(packingCostExcludingVAT)
    .times(
      new Big(marginPercentOnBaseCost)
        .div(100)
    )
    .times(countryVATRate.applyCoeff);
  grossMarginExcludingVAT = grossMarginIncludingVAT.times(countryVATRate.reverseCoeff);
  grossMarginVAT = grossMarginIncludingVAT.times(countryVATRate.reverseExtractCoeff);

  // Shipping cost
  if (
    supplierName === 'aliexpress'
    || forPlatform === 'direct-sale'
  ) {
    // With Aliexpress items, shipping is included in the product price
    shippingServiceSelected = null;
  } else {
    const possibleShippingServices = getShippingServicesForPackage({
      packageWeight,
      packageSize1,
      packageSize2,
      packageSize3,
      orderPlatform: forPlatform,
      supplier: supplierName,
      origin,
      destination,
    });
    // Sorted by price, the first one is the cheapest
    shippingServiceSelected = possibleShippingServices[0];

    shippingCostIncludingVAT = new Big(shippingServiceSelected.rate.priceWithVAT);
    shippingCostExcludingVAT = new Big(shippingServiceSelected.rate.priceWithoutVAT);
    shippingCostVAT = new Big(shippingServiceSelected.rate.priceWithVAT).minus(shippingServiceSelected.rate.priceWithoutVAT);
  }

  // Return shipping cost
  // TODO: calculate based on shipping service, needs also to check is shipping service is used for return as well
  let returnShippingCostIncludingVAT = new Big(0);
  let returnShippingCostExcludingVAT = new Big(0);
  let returnShippingCostVAT = new Big(0);
  if (
    supplierName === 'aliexpress'
    || forPlatform === 'direct-sale'
  ) {
    returnShippingCostIncludingVAT = new Big(0);
    returnShippingCostExcludingVAT = new Big(0);
    returnShippingCostVAT = new Big(0);
  } else {
    returnShippingCostIncludingVAT = new Big(0.5).times(countryVATRate.applyCoeff);
    returnShippingCostExcludingVAT = new Big(0.5);
    returnShippingCostVAT = new Big(0.5).times(countryVATRate.applyExtractCoeff);
  }

  // Calculate channel fee and final price depending on the platform
  if (
    forPlatform === 'temu'
  ) {
    const temuApplyCoeff = new Big(1.11); // 11% fee
    const temuApplyExtractCoeff = new Big(0.11); // 11% fee

    const sumBeforeChannelFeeAndVAT = productCostExcludingVAT
      .plus(packingCostExcludingVAT)
      .plus(grossMarginExcludingVAT)
      .plus(shippingCostExcludingVAT)
      .plus(returnShippingCostExcludingVAT);
    // If the retail price is below the free above price,
    // We will remove the shipping price from the retail 
    retailPriceIncludingVAT = sumBeforeChannelFeeAndVAT
      .times(temuApplyCoeff)
      .times(countryVATRate.applyCoeff) // Apply VAT
    retailPriceExcludingVAT = retailPriceIncludingVAT.times(countryVATRate.reverseCoeff);
    retailPriceVAT = retailPriceIncludingVAT.times(countryVATRate.reverseExtractCoeff);

    // Temu does not apply any additional fee at checkout
    extraFeeIncludingVAT = new Big(0);
    extraFeeExcludingVAT = new Big(0);
    extraFeeVAT = new Big(0);

    // Align with Target: use listing price for threshold (free shipping when listing >= freeAbovePrice)
    const listingPriceIfSplit = sumBeforeChannelFeeAndVAT
      .minus(temuShippingPolicy.price.amountWithoutVAT)
      .times(temuApplyCoeff)
      .times(countryVATRate.applyCoeff);
    if (listingPriceIfSplit.lt(temuShippingPolicy.freeAbovePrice)) {
      // In this case, we will remove the extra shipping cost from the base price
      // That also applies to the temu fee
      const sumWithoutShipping = sumBeforeChannelFeeAndVAT
        .minus(temuShippingPolicy.price.amountWithoutVAT);

      retailPriceIncludingVAT = sumWithoutShipping
        .times(temuApplyCoeff)
        .times(countryVATRate.applyCoeff)
        .plus(temuShippingPolicy.price.totalAmount)
      retailPriceExcludingVAT = retailPriceIncludingVAT.times(countryVATRate.reverseCoeff);
      retailPriceVAT = retailPriceIncludingVAT.times(countryVATRate.reverseExtractCoeff);

      // This is base price to set on Temu
      setExternalPrice = sumWithoutShipping;

      // Listing price is the price displayed on the listing without shipping extra cost
      listingPriceIncludingVAT = sumWithoutShipping
        .times(temuApplyCoeff)
        .times(countryVATRate.applyCoeff);

      listingPriceExcludingVAT = listingPriceIncludingVAT.times(countryVATRate.reverseCoeff);
      listingPriceVAT = listingPriceIncludingVAT.times(countryVATRate.reverseExtractCoeff);

      // This is what will the customer pay for shipping
      extraShippingPriceIncludingVAT = new Big(temuShippingPolicy.price.totalAmount);
      extraShippingPriceExcludingVAT = new Big(temuShippingPolicy.price.amountWithoutVAT);
      extraShippingPriceVAT = new Big(temuShippingPolicy.price.amountVAT);
    }
    // If the retail price is above the free above price, shipping must be included in the price
    else {
      // This is base price to set on Temu
      setExternalPrice = sumBeforeChannelFeeAndVAT;

      // Listing price is the price displayed on the listing without shipping extra cost
      listingPriceIncludingVAT = retailPriceIncludingVAT;
      listingPriceExcludingVAT = retailPriceExcludingVAT;
      listingPriceVAT = retailPriceVAT;

      // This is what will the customer pay for shipping
      extraShippingPriceIncludingVAT = new Big(0);
      extraShippingPriceExcludingVAT = new Big(0);
      extraShippingPriceVAT = new Big(0);
    }

    // Channel fee is calculated on the base price sent to Temu
    channelFeeIncludingVAT = setExternalPrice.times(temuApplyExtractCoeff).times(countryVATRate.applyCoeff);
    channelFeeExcludingVAT = channelFeeIncludingVAT.times(countryVATRate.reverseCoeff);
    channelFeeVAT = channelFeeIncludingVAT.times(countryVATRate.reverseExtractCoeff);
  }
  else if (
    forPlatform === 'aliexpress'
  ) {
    const aliexpressApplyCoeff = new Big(1.11); // 11% fee to be more precise later
    const aliexpressApplyExtractCoeff = new Big(0.11); // 11% fee to be more precise later

    const sumBeforeChannelFee = productCostExcludingVAT
      .plus(packingCostExcludingVAT)
      .plus(grossMarginExcludingVAT)
      .plus(shippingCostExcludingVAT)
      .plus(returnShippingCostExcludingVAT)
      .times(countryVATRate.applyCoeff);
    // If the retail price is below the free above price,
    // We will remove the shipping price from the retail 
    retailPriceIncludingVAT = sumBeforeChannelFee
      .times(aliexpressApplyCoeff)
    retailPriceExcludingVAT = retailPriceIncludingVAT.times(countryVATRate.reverseCoeff);
    retailPriceVAT = retailPriceIncludingVAT.times(countryVATRate.reverseExtractCoeff);

    // Aliexpress does not apply any additional fee at checkout
    extraFeeIncludingVAT = new Big(0);
    extraFeeExcludingVAT = new Big(0);
    extraFeeVAT = new Big(0);

    // This is base price to set on Aliexpress, includes VAT
    setExternalPrice = sumBeforeChannelFee

    // Listing price is the price displayed on the listing without shipping extra cost
    listingPriceIncludingVAT = retailPriceIncludingVAT;
    listingPriceExcludingVAT = retailPriceExcludingVAT;
    listingPriceVAT = retailPriceVAT;

    // This is what will the customer pay for shipping
    extraShippingPriceIncludingVAT = new Big(0);
    extraShippingPriceExcludingVAT = new Big(0);
    extraShippingPriceVAT = new Big(0);

    // Channel fee is calculated on the base price sent to Aliexpress
    channelFeeIncludingVAT = setExternalPrice.times(aliexpressApplyExtractCoeff);
    channelFeeExcludingVAT = channelFeeIncludingVAT.times(countryVATRate.reverseCoeff);
    channelFeeVAT = channelFeeIncludingVAT.times(countryVATRate.reverseExtractCoeff);
  }
  else if (
    forPlatform === 'leboncoin'
  ) {
    const leboncoinCoeff = new Big(0.14); // 14% fee VAT incl TODO: improve by category
    const channelAndVATCoeff = new Big(1)
        .minus(leboncoinCoeff.times(countryVATRate.reverseCoeff))
        .minus(countryVATRate.reverseExtractCoeff)

    const sumBeforeShippingCost = productCostExcludingVAT
      .plus(packingCostExcludingVAT)
      .plus(grossMarginExcludingVAT)
      .plus(returnShippingCostExcludingVAT);

    listingPriceIncludingVAT = sumBeforeShippingCost
      .div(channelAndVATCoeff);

    listingPriceExcludingVAT = listingPriceIncludingVAT.times(countryVATRate.reverseCoeff);
    listingPriceVAT = listingPriceIncludingVAT.times(countryVATRate.reverseExtractCoeff);

    setExternalPrice = listingPriceIncludingVAT;
    
    const leboncoinCustomerFee = listingPriceIncludingVAT.times(0.05).plus(0.70); // 5% fee + 0.70€ fixed fee, VAT incl
    retailPriceIncludingVAT = listingPriceIncludingVAT
      .plus(shippingCostIncludingVAT) // shipping cost added, VAT incl
      .plus(leboncoinCustomerFee);
    retailPriceExcludingVAT = retailPriceIncludingVAT.times(countryVATRate.reverseCoeff);
    retailPriceVAT = retailPriceIncludingVAT.times(countryVATRate.reverseExtractCoeff);

    extraShippingPriceIncludingVAT = shippingCostIncludingVAT;
    extraShippingPriceExcludingVAT = shippingCostExcludingVAT;
    extraShippingPriceVAT = shippingCostVAT;

    extraFeeIncludingVAT = new Big(leboncoinCustomerFee);
    extraFeeExcludingVAT = new Big(leboncoinCustomerFee).times(countryVATRate.reverseCoeff);
    extraFeeVAT = new Big(leboncoinCustomerFee).times(countryVATRate.reverseExtractCoeff);

    channelFeeIncludingVAT = listingPriceIncludingVAT.times(leboncoinCoeff).plus(leboncoinCustomerFee);
    channelFeeExcludingVAT = channelFeeIncludingVAT.times(countryVATRate.reverseCoeff);
    channelFeeVAT = channelFeeIncludingVAT.times(countryVATRate.reverseExtractCoeff);
  }
  else if (forPlatform === 'ebay') {
    const ebayCoeff = new Big(0.15); // 15% fee TODO: improve by category + ads
    const channelAndVATCoeff = new Big(1)
        .minus(ebayCoeff.times(countryVATRate.reverseCoeff))
        .minus(countryVATRate.reverseExtractCoeff)

    retailPriceIncludingVAT = productCostExcludingVAT
      .plus(packingCostExcludingVAT)
      .plus(grossMarginExcludingVAT)
      .plus(returnShippingCostExcludingVAT)
      .plus(shippingCostExcludingVAT)
      // Commission is also taken on shipping cost
      .div(channelAndVATCoeff);
    retailPriceExcludingVAT = retailPriceIncludingVAT.times(countryVATRate.reverseCoeff);
    retailPriceVAT = retailPriceIncludingVAT.times(countryVATRate.reverseExtractCoeff);

    // As shipping cost is included in product price, listing price is the same as retail price
    listingPriceIncludingVAT = retailPriceIncludingVAT;
    listingPriceExcludingVAT = retailPriceExcludingVAT;
    listingPriceVAT = retailPriceVAT;

    setExternalPrice = listingPriceIncludingVAT;

    // As shipping cost is included in product price, extra shipping price is 0
    extraShippingPriceIncludingVAT = new Big(0);
    extraShippingPriceExcludingVAT = new Big(0);
    extraShippingPriceVAT = new Big(0);

    // eBay does not apply any additional fee at checkout
    extraFeeIncludingVAT = new Big(0);
    extraFeeExcludingVAT = new Big(0);
    extraFeeVAT = new Big(0);

    channelFeeIncludingVAT = listingPriceIncludingVAT.times(ebayCoeff);
    channelFeeExcludingVAT = channelFeeIncludingVAT.times(countryVATRate.reverseCoeff);
    channelFeeVAT = channelFeeIncludingVAT.times(countryVATRate.reverseExtractCoeff);
  }
  else if (forPlatform === 'direct-sale') {

    const base = productCostExcludingVAT
      .plus(packingCostExcludingVAT);

    const directSaleExtraAmount = base.times(0.15).times(countryVATRate.applyCoeff); // 7% additional fee as we don't have other fees
    grossMarginIncludingVAT = grossMarginIncludingVAT.plus(directSaleExtraAmount);
    grossMarginExcludingVAT = grossMarginIncludingVAT.times(countryVATRate.reverseCoeff);
    grossMarginVAT = grossMarginIncludingVAT.times(countryVATRate.reverseExtractCoeff);

    retailPriceIncludingVAT = base
      .plus(grossMarginExcludingVAT)
      .times(countryVATRate.applyCoeff)
    retailPriceVAT = retailPriceIncludingVAT.times(countryVATRate.reverseExtractCoeff);
    retailPriceExcludingVAT = retailPriceIncludingVAT.times(countryVATRate.reverseCoeff);
    
    listingPriceIncludingVAT = retailPriceIncludingVAT;
    listingPriceExcludingVAT = retailPriceExcludingVAT;
    listingPriceVAT = retailPriceVAT;

    setExternalPrice = listingPriceIncludingVAT;

    extraShippingPriceIncludingVAT = new Big(0);
    extraShippingPriceExcludingVAT = new Big(0);
    extraShippingPriceVAT = new Big(0);

    extraFeeIncludingVAT = new Big(0);
    extraFeeExcludingVAT = new Big(0);
    extraFeeVAT = new Big(0);

    channelFeeIncludingVAT = new Big(0);
    channelFeeExcludingVAT = new Big(0);
    channelFeeVAT = new Big(0);

    shippingCostIncludingVAT = new Big(0);
    shippingCostExcludingVAT = new Big(0);
    shippingCostVAT = new Big(0);
  }

  if (listingPriceRoundingMethod) {
    const roundedListingPrice = roundPrice(listingPriceIncludingVAT, listingPriceRoundingMethod);
    return calculatePrices_WithTargetPriceFn({
      targetListingPriceIncludingVat: roundedListingPrice.toNumber(),
      productCostExcludingVAT: productCostExcludingVATArg,
      packingCostExcludingVAT: packingCostExcludingVATArg,
      forPlatform,
      supplierName,
      origin,
      destination,
      packageWeight,
      packageSize1,
      packageSize2,
      packageSize3,
    });
  }



  return {
    retailPriceIncludingVAT,
    retailPriceExcludingVAT,
    retailPriceVAT,
    listingPriceIncludingVAT,
    listingPriceExcludingVAT,
    listingPriceVAT,
    extraShippingPriceIncludingVAT,
    extraShippingPriceExcludingVAT,
    extraShippingPriceVAT,
    extraFeeIncludingVAT,
    extraFeeExcludingVAT,
    extraFeeVAT,
    setExternalPrice,
    grossMarginIncludingVAT,
    grossMarginExcludingVAT,
    grossMarginVAT,
    channelFeeIncludingVAT,
    channelFeeExcludingVAT,
    channelFeeVAT,
    packingCostIncludingVAT,
    packingCostExcludingVAT,
    packingCostVAT,
    productCostIncludingVAT,
    productCostExcludingVAT,
    productCostVAT,
    shippingServiceSelected,
    shippingCostIncludingVAT,
    shippingCostExcludingVAT,
    shippingCostVAT,
    returnShippingCostIncludingVAT,
    returnShippingCostExcludingVAT,
    returnShippingCostVAT,
  };
}

// Generated with reverse AI
function calculatePrices_WithTargetPriceFn({
    targetListingPriceIncludingVat,
    productCostExcludingVAT: productCostExcludingVATArg,
    packingCostExcludingVAT: packingCostExcludingVATArg,

    forPlatform,
    supplierName,
    origin,
    destination,

    packageWeight,
    packageSize1,
    packageSize2,
    packageSize3,
  }: Omit<CalculatePricesArgs, 'marginPercentOnBaseCost' | 'listingPriceRoundingMethod'>
): Omit<ProductPrices, 'listingKey' | 'listingLabel'> {
  const countryVATRate = VATRatePerCountry[destination];
  const temuShippingPolicy = temuShippingPolicyPerCountry[destination];

  let packingCostIncludingVAT = new Big(packingCostExcludingVATArg).times(countryVATRate.applyCoeff);
  let packingCostExcludingVAT = new Big(packingCostExcludingVATArg);
  let packingCostVAT = packingCostIncludingVAT.minus(new Big(packingCostExcludingVATArg));

  let productCostIncludingVAT = new Big(productCostExcludingVATArg).times(countryVATRate.applyCoeff);
  let productCostExcludingVAT = new Big(productCostExcludingVATArg);
  let productCostVAT = productCostIncludingVAT.minus(productCostExcludingVATArg);

  let shippingCostIncludingVAT = new Big(0);
  let shippingCostExcludingVAT = new Big(0);
  let shippingCostVAT = new Big(0);
  let shippingServiceSelected: ReturnType<typeof getShippingServicesForPackage>[0] | null = null;

  if (supplierName === 'aliexpress' || forPlatform === 'direct-sale') {
    shippingServiceSelected = null;
  } else {
    const possibleShippingServices = getShippingServicesForPackage({
      packageWeight,
      packageSize1,
      packageSize2,
      packageSize3,
      orderPlatform: forPlatform,
      supplier: supplierName,
      origin,
      destination,
    });
    shippingServiceSelected = possibleShippingServices[0];
    shippingCostIncludingVAT = new Big(shippingServiceSelected.rate.priceWithVAT);
    shippingCostExcludingVAT = new Big(shippingServiceSelected.rate.priceWithoutVAT);
    shippingCostVAT = new Big(shippingServiceSelected.rate.priceWithVAT).minus(shippingServiceSelected.rate.priceWithoutVAT);
  }

  let returnShippingCostIncludingVAT: Big;
  let returnShippingCostExcludingVAT: Big;
  let returnShippingCostVAT: Big;
  if (
    supplierName === 'aliexpress'
    || forPlatform === 'direct-sale'
  ) {
    returnShippingCostIncludingVAT = new Big(0);
    returnShippingCostExcludingVAT = new Big(0);
    returnShippingCostVAT = new Big(0);
  } else {
    returnShippingCostIncludingVAT = new Big(0.5).times(countryVATRate.applyCoeff);
    returnShippingCostExcludingVAT = new Big(0.5);
    returnShippingCostVAT = new Big(0.5).times(countryVATRate.applyExtractCoeff);
  }

  const targetListingPrice = new Big(targetListingPriceIncludingVat);

  let listingPriceIncludingVAT = targetListingPrice;
  let listingPriceExcludingVAT = targetListingPrice.times(countryVATRate.reverseCoeff);
  let listingPriceVAT = targetListingPrice.times(countryVATRate.reverseExtractCoeff);

  let retailPriceIncludingVAT = new Big(0);
  let retailPriceExcludingVAT = new Big(0);
  let retailPriceVAT = new Big(0);
  let extraShippingPriceIncludingVAT = new Big(0);
  let extraShippingPriceExcludingVAT = new Big(0);
  let extraShippingPriceVAT = new Big(0);
  let extraFeeIncludingVAT = new Big(0);
  let extraFeeExcludingVAT = new Big(0);
  let extraFeeVAT = new Big(0);
  let setExternalPrice = new Big(0);
  let grossMarginIncludingVAT = new Big(0);
  let grossMarginExcludingVAT = new Big(0);
  let grossMarginVAT = new Big(0);
  let channelFeeIncludingVAT = new Big(0);
  let channelFeeExcludingVAT = new Big(0);
  let channelFeeVAT = new Big(0);

  if (
    forPlatform === 'temu'
  ) {
    const temuApplyCoeff = new Big(1.11);
    const temuApplyExtractCoeff = new Big(0.11);

    const isFreeShipping = targetListingPrice.gte(temuShippingPolicy.freeAbovePrice);

    let sumBeforeChannelFeeAndVAT: Big;
    if (isFreeShipping) {
      sumBeforeChannelFeeAndVAT = targetListingPrice
        .div(temuApplyCoeff)
        .times(countryVATRate.reverseCoeff);
      setExternalPrice = sumBeforeChannelFeeAndVAT;
      retailPriceIncludingVAT = targetListingPrice;
      retailPriceExcludingVAT = listingPriceExcludingVAT;
      retailPriceVAT = listingPriceVAT;
      extraShippingPriceIncludingVAT = new Big(0);
      extraShippingPriceExcludingVAT = new Big(0);
      extraShippingPriceVAT = new Big(0);
    } else {
      const sumWithoutShipping = targetListingPrice
        .div(temuApplyCoeff)
        .times(countryVATRate.reverseCoeff);
      sumBeforeChannelFeeAndVAT = sumWithoutShipping.plus(temuShippingPolicy.price.amountWithoutVAT);
      setExternalPrice = sumWithoutShipping;
      retailPriceIncludingVAT = targetListingPrice.plus(temuShippingPolicy.price.totalAmount);
      retailPriceExcludingVAT = retailPriceIncludingVAT.times(countryVATRate.reverseCoeff);
      retailPriceVAT = retailPriceIncludingVAT.times(countryVATRate.reverseExtractCoeff);
      extraShippingPriceIncludingVAT = new Big(temuShippingPolicy.price.totalAmount);
      extraShippingPriceExcludingVAT = new Big(temuShippingPolicy.price.amountWithoutVAT);
      extraShippingPriceVAT = new Big(temuShippingPolicy.price.amountVAT);
    }

    grossMarginExcludingVAT = sumBeforeChannelFeeAndVAT
      .minus(productCostExcludingVAT)
      .minus(packingCostExcludingVAT)
      .minus(shippingCostExcludingVAT)
      .minus(returnShippingCostExcludingVAT);
    grossMarginIncludingVAT = grossMarginExcludingVAT.times(countryVATRate.applyCoeff);
    grossMarginVAT = grossMarginIncludingVAT.times(countryVATRate.reverseExtractCoeff);

    extraFeeIncludingVAT = new Big(0);
    extraFeeExcludingVAT = new Big(0);
    extraFeeVAT = new Big(0);

    channelFeeIncludingVAT = setExternalPrice.times(temuApplyExtractCoeff).times(countryVATRate.applyCoeff);
    channelFeeExcludingVAT = channelFeeIncludingVAT.times(countryVATRate.reverseCoeff);
    channelFeeVAT = channelFeeIncludingVAT.times(countryVATRate.reverseExtractCoeff);
  }
  else if (
    forPlatform === 'aliexpress'
  ) {
    const aliexpressApplyCoeff = new Big(1.11); // 11% fee to be more precise later
    const aliexpressApplyExtractCoeff = new Big(0.11); // 11% fee to be more precise later

    retailPriceIncludingVAT = targetListingPrice;
    retailPriceExcludingVAT = listingPriceExcludingVAT;
    retailPriceVAT = listingPriceVAT;

    const sumBeforeChannelFee = targetListingPrice.div(aliexpressApplyCoeff);
    setExternalPrice = sumBeforeChannelFee;

    grossMarginExcludingVAT = sumBeforeChannelFee
      .times(countryVATRate.reverseCoeff)
      .minus(productCostExcludingVAT)
      .minus(packingCostExcludingVAT)
      .minus(shippingCostExcludingVAT)
      .minus(returnShippingCostExcludingVAT);
    grossMarginIncludingVAT = grossMarginExcludingVAT.times(countryVATRate.applyCoeff);
    grossMarginVAT = grossMarginIncludingVAT.times(countryVATRate.reverseExtractCoeff);

    // Aliexpress does not apply any additional fee at checkout
    extraFeeIncludingVAT = new Big(0);
    extraFeeExcludingVAT = new Big(0);
    extraFeeVAT = new Big(0);

    // This is what the customer pays for shipping
    extraShippingPriceIncludingVAT = new Big(0);
    extraShippingPriceExcludingVAT = new Big(0);
    extraShippingPriceVAT = new Big(0);

    // Channel fee is calculated on the base price sent to Aliexpress
    channelFeeIncludingVAT = setExternalPrice.times(aliexpressApplyExtractCoeff);
    channelFeeExcludingVAT = channelFeeIncludingVAT.times(countryVATRate.reverseCoeff);
    channelFeeVAT = channelFeeIncludingVAT.times(countryVATRate.reverseExtractCoeff);
  }
  else if (forPlatform === 'leboncoin') {
    const leboncoinCoeff = new Big(0.14);
    const channelAndVATCoeff = new Big(1)
      .minus(leboncoinCoeff.times(countryVATRate.reverseCoeff))
      .minus(countryVATRate.reverseExtractCoeff);

    setExternalPrice = listingPriceIncludingVAT;

    const sumBeforeShippingCost = listingPriceIncludingVAT.times(channelAndVATCoeff);
    grossMarginExcludingVAT = sumBeforeShippingCost
      .minus(productCostExcludingVAT)
      .minus(packingCostExcludingVAT)
      .minus(returnShippingCostExcludingVAT);
    grossMarginIncludingVAT = grossMarginExcludingVAT.times(countryVATRate.applyCoeff);
    grossMarginVAT = grossMarginIncludingVAT.times(countryVATRate.reverseExtractCoeff);

    const leboncoinCustomerFee = listingPriceIncludingVAT.times(0.05).plus(0.70);
    retailPriceIncludingVAT = listingPriceIncludingVAT
      .plus(shippingCostIncludingVAT)
      .plus(leboncoinCustomerFee);
    retailPriceExcludingVAT = retailPriceIncludingVAT.times(countryVATRate.reverseCoeff);
    retailPriceVAT = retailPriceIncludingVAT.times(countryVATRate.reverseExtractCoeff);

    extraShippingPriceIncludingVAT = shippingCostIncludingVAT;
    extraShippingPriceExcludingVAT = shippingCostExcludingVAT;
    extraShippingPriceVAT = shippingCostVAT;

    extraFeeIncludingVAT = new Big(leboncoinCustomerFee);
    extraFeeExcludingVAT = new Big(leboncoinCustomerFee).times(countryVATRate.reverseCoeff);
    extraFeeVAT = new Big(leboncoinCustomerFee).times(countryVATRate.reverseExtractCoeff);

    channelFeeIncludingVAT = listingPriceIncludingVAT.times(leboncoinCoeff).plus(leboncoinCustomerFee);
    channelFeeExcludingVAT = channelFeeIncludingVAT.times(countryVATRate.reverseCoeff);
    channelFeeVAT = channelFeeIncludingVAT.times(countryVATRate.reverseExtractCoeff);
  } else if (forPlatform === 'ebay') {
    const ebayCoeff = new Big(0.15);
    const channelAndVATCoeff = new Big(1)
      .minus(ebayCoeff.times(countryVATRate.reverseCoeff))
      .minus(countryVATRate.reverseExtractCoeff);

    retailPriceIncludingVAT = targetListingPrice;
    retailPriceExcludingVAT = listingPriceExcludingVAT;
    retailPriceVAT = listingPriceVAT;
    setExternalPrice = listingPriceIncludingVAT;

    const sumBeforeChannelFeeAndVAT = targetListingPrice.times(channelAndVATCoeff);
    grossMarginExcludingVAT = sumBeforeChannelFeeAndVAT
      .minus(productCostExcludingVAT)
      .minus(packingCostExcludingVAT)
      .minus(returnShippingCostExcludingVAT)
      .minus(shippingCostExcludingVAT);
    grossMarginIncludingVAT = grossMarginExcludingVAT.times(countryVATRate.applyCoeff);
    grossMarginVAT = grossMarginIncludingVAT.times(countryVATRate.reverseExtractCoeff);

    extraShippingPriceIncludingVAT = new Big(0);
    extraShippingPriceExcludingVAT = new Big(0);
    extraShippingPriceVAT = new Big(0);

    extraFeeIncludingVAT = new Big(0);
    extraFeeExcludingVAT = new Big(0);
    extraFeeVAT = new Big(0);

    channelFeeIncludingVAT = listingPriceIncludingVAT.times(ebayCoeff);
    channelFeeExcludingVAT = channelFeeIncludingVAT.times(countryVATRate.reverseCoeff);
    channelFeeVAT = channelFeeIncludingVAT.times(countryVATRate.reverseExtractCoeff);
  } else if (forPlatform === 'direct-sale') {
    const base = productCostExcludingVAT.plus(packingCostExcludingVAT);
    grossMarginIncludingVAT = targetListingPrice.minus(base.times(countryVATRate.applyCoeff));
    grossMarginExcludingVAT = grossMarginIncludingVAT.times(countryVATRate.reverseCoeff);
    grossMarginVAT = grossMarginIncludingVAT.times(countryVATRate.reverseExtractCoeff);

    retailPriceIncludingVAT = targetListingPrice;
    retailPriceExcludingVAT = listingPriceExcludingVAT;
    retailPriceVAT = listingPriceVAT;
    setExternalPrice = listingPriceIncludingVAT;

    extraShippingPriceIncludingVAT = new Big(0);
    extraShippingPriceExcludingVAT = new Big(0);
    extraShippingPriceVAT = new Big(0);

    extraFeeIncludingVAT = new Big(0);
    extraFeeExcludingVAT = new Big(0);
    extraFeeVAT = new Big(0);

    channelFeeIncludingVAT = new Big(0);
    channelFeeExcludingVAT = new Big(0);
    channelFeeVAT = new Big(0);

    shippingCostIncludingVAT = new Big(0);
    shippingCostExcludingVAT = new Big(0);
    shippingCostVAT = new Big(0);
  }

  return {
    retailPriceIncludingVAT,
    retailPriceExcludingVAT,
    retailPriceVAT,
    listingPriceIncludingVAT,
    listingPriceExcludingVAT,
    listingPriceVAT,
    extraShippingPriceIncludingVAT,
    extraShippingPriceExcludingVAT,
    extraShippingPriceVAT,
    extraFeeIncludingVAT,
    extraFeeExcludingVAT,
    extraFeeVAT,
    setExternalPrice,
    grossMarginIncludingVAT,
    grossMarginExcludingVAT,
    grossMarginVAT,
    channelFeeIncludingVAT,
    channelFeeExcludingVAT,
    channelFeeVAT,
    packingCostIncludingVAT,
    packingCostExcludingVAT,
    packingCostVAT,
    productCostIncludingVAT,
    productCostExcludingVAT,
    productCostVAT,
    shippingServiceSelected,
    shippingCostIncludingVAT,
    shippingCostExcludingVAT,
    shippingCostVAT,
    returnShippingCostIncludingVAT,
    returnShippingCostExcludingVAT,
    returnShippingCostVAT,
  };
}

export const calculatePrices = memoizee(calculatePricesFn);


function roundPrice(
  price: Big,
  priceRoundingMethod: PriceRoundingMethod
) {
  if (priceRoundingMethod === PriceRoundingMethod.NearestFiftyCents) {
    return price.times(2).round().div(2);
  } else if (priceRoundingMethod === PriceRoundingMethod.UpperFiftyCents) {
    return price.times(2).round(0, Big.roundUp).div(2);
  } else if (priceRoundingMethod === PriceRoundingMethod.LowerFiftyCents) {
    return price.times(2).round(0, Big.roundDown).div(2);
  }
  else {
    throw new Error(`Invalid price rounding method: ${priceRoundingMethod}`);
  }
}

export function getVATAmount({
  price,
  vatRatePercent,
  vatRateApplyCoeff,
}: {
  price: Big | number | string,
  vatRatePercent?: Big | number | string,
  vatRateApplyCoeff?: Big | number | string
}) {
  if (vatRatePercent && vatRateApplyCoeff) {
    throw new Error('Only one of vatRatePercent or vatRateApplyCoeff must be provided');
  }

  if (vatRatePercent) {
    return new Big(price).times(new Big(vatRatePercent).div(100));
  }
  else if (vatRateApplyCoeff) {
    return new Big(price).times(new Big(vatRateApplyCoeff));
  }
  else {
    throw new Error('Either vatRatePercent or vatRateApplyCoeff must be provided');
  }
}

export function splitAmount(amount: Big | number | string, numberOfItems: number) {
  const splitAmount = new Big(amount).div(numberOfItems);
  const remainderAmount = new Big(amount).minus(splitAmount.times(numberOfItems));
  return {
    splitAmount,
    remainderAmount,
    lastAmount: splitAmount.plus(remainderAmount),
  };
}